<!--
  Component: Concatenation Item.
  Path:      Field - Calculation - Concatenation - Item.
  Concatenation Item types:
    - Literal
      - User entered text value.
    - MDM Field
      - User selected Field from current table.
    - MDM Field From Related Table
      - User selected Lookup Field from current table.
      - User selecetd Field in target table (for Lookup).
-->
<template>
  <tr
    :key="`concatenation-value-${index + 1}`"
    class="concatenation-value text-center mb-2"
  >
    <!-- Drag Handle cell. -->
    <td class="px-2">
      <div class="handle">
        <font-awesome-icon :icon="['fas', 'bars']" />
      </div>
    </td>
    <!-- Concatenation Item properties cell. -->
    <td class="pb-2">
      <div class="row">
        <div class="col-sm-4">
          <b-form-select
            :id="`concatenationValue-type-${index + 1}`"
            v-model="concatenationValue.itemType"
            class="mr-sm-2"
            :options="itemTypes"
            @change="onChangeItemType"
          />
        </div>
        <div class="col-sm-4">
          <!-- String Literal value input. -->
          <b-input
            v-if="isItemTypeStringLiteral"
            :id="`concatenationValue-value-${index + 1}`"
            v-model="concatenationValue.itemValue"
            class="mr-sm-2"
            placeholder="Item Value"
          />
          <!-- MDM Field field dropdown. -->
          <b-form-select
            v-if="isItemTypeMdmField"
            :id="`concatenationValue-value-${index + 1}`"
            v-model="concatenationValue.itemValue"
            class="mr-sm-2"
            :options="mdmFieldOptions"
            value-field="fieldGuid"
            text-field="displayName"
          />
          <!-- Related Table Lookup Field dropdown. -->
          <b-form-select
            v-if="isItemTypeMdmFieldFromRelatedTable"
            :id="`concatenationValue-value-${index + 1}`"
            v-model="concatenationValue.itemValue.lookupFieldGuid"
            class="mr-sm-2"
            :options="tableLookupFields"
            value-field="fieldGuid"
            text-field="displayName"
            @change="onChangeLookupField"
          />
        </div>
        <div
          v-if="isItemTypeMdmFieldFromRelatedTable"
          class="col-sm-4"
        >
          <!-- Related Table field  dropdown. -->
          <b-form-select
            :id="`concatenationValue-lookupField-${index + 1}`"
            v-model="concatenationValue.itemValue.fieldToDisplayGuid"
            class="mr-sm-2"
            :options="relatedTableFieldOptions"
            value-field="fieldGuid"
            text-field="displayName"
          />
        </div>
      </div>
    </td>
    <!-- Actions cell. -->
    <td>
      <div class="action-buttons text-nowrap">
        <b-button
        :id="`concatenationValue-delete-${index + 1}`"
        variant="link"
          pill
          class="btn-delete text-danger"
          @click="onDeleteClick"
        >
          <font-awesome-icon :icon="'trash'" />
        </b-button>
      </div>
    </td>
  </tr>
</template>

<script>

import {createNamespacedHelpers} from 'vuex';
const {mapState} = createNamespacedHelpers('dataManagement');

export default {

  name: 'ConcatenationItem',

  inject: [
    /* Current Table. [from CalculationModel] */
    'table',
    /* All Fields across all Modules/Folders. [from CalculationModel] */
    'fields',
    /* Entity utility functions etc. [from CalculationModel] */
    'entityUtils',
  ],

  props: {
    table: Object,
    index: Number,
    concatenationValue: Object,
  },

  data() {
    return {
      itemTypes: this.entityUtils.concatenationTypeOptions,
    };
  },

  /**
   * Watch for changes to reactive properrties.
   * - NB Items moved as a result of dragging are swapped and trigger a corresonding Watch.
   * - Hence, any Watch on ConcatenationValue properties must take care.
   */
  watch: {
    ['concatenationValue.itemType']: {
      handler(newValue, oldValue) {
        //console.log(`concatenationValue.itemType ${newValue}. ${oldValue}.`);
      },
      deep: true,
      immediate: true,
    },
  },

  computed: {
    ...mapState({
      fields: state => state.fields,
    }),
    /** Fields in the current table for MDM Field Item Value dropdown options. */
    mdmFieldOptions() {
      return [
        {
          fieldGuid: null,
          displayName: 'Please Select...',
        },
        ...this.table.fields.filter(fld => this.entityUtils.fieldCanBeConcatenationMdmField(fld)),
      ];
    },
    isItemTypeStringLiteral() {
      return this.concatenationValue.itemType === this.entityUtils.concatenationTypes.stringLiteral;
    },
    isItemTypeMdmField() {
      return this.concatenationValue.itemType === this.entityUtils.concatenationTypes.mdmField;
    },
    isItemTypeMdmFieldFromRelatedTable() {
      return this.concatenationValue.itemType === this.entityUtils.concatenationTypes.mdmFieldFromRelatedTable;
    },
    /** Lookup Fields in the current Table for 'Related Table' dropdown options. */
    tableLookupFields() {
      return this.table.fields.filter(field => this.entityUtils.dataTypeIsLookup(field.dataTypeName));
    },
    relatedTableFieldOptions(){
      /* Identify the 'Lookup Field'. */
      const lookupField = this.table.fields.find(f => f.fieldGuid === this.concatenationValue.itemValue?.lookupFieldGuid);
      if (lookupField) {
        /* Identify the 'Related Field' for the 'Lookup Field'. */
        const relatedField = this.fields.data.find(elem => elem.fieldGuid === lookupField.dataTypeJSON.FieldToDisplayGuid);
        if (relatedField) {
          /* Return all Fields in the same Table as the 'Related Field'. */
          const options = this.fields.data.filter(fld => this.entityUtils.fieldCanBeConcatenationFieldToDisplay(fld, relatedField));
          /* If the previously selected 'Related Field' is no longer in the list, clear it.*/
          if (!options.find(elem => elem.fieldGuid === this.concatenationValue.itemValue.fieldToDisplayGuid))
            this.concatenationValue.itemValue.fieldToDisplayGuid = '';
          return options;
        }
      }
      return []
    },
  },

  methods: {
    /** User has selected Item Type in the dropdown. */
    onChangeItemType(){
      //console.log(`onChangeItemType Index: ${this.index}. Type: ${this.concatenationValueitemType}.`);
      if (this.isItemTypeMdmFieldFromRelatedTable) {
          /* 'Related Table' ItemValue carries two separate properties. */
          this.$set(this.concatenationValue, 'itemValue', {});
          this.$set(this.concatenationValue.itemValue, 'lookupFieldGuid', '');
          this.$set(this.concatenationValue.itemValue, 'fieldToDisplayGuid', '');
        } else {
          /* Otherwise, ItemValue is a String value. Clear subordinate reactive properties if present. */
          if (this.concatenationValue?.itemValue?.lookupFieldGuid) this.$delete(this.concatenationValue.itemValue, 'lookupFieldGuid');
          if (this.concatenationValue?.itemValue?.fieldToDisplayGuid) this.$delete(this.concatenationValue.itemValue, 'fieldToDisplayGuid');
          this.$set(this.concatenationValue, 'itemValue', '');
        }
    },
    /** 'Lookup Field' has been selected by the user. */
    onChangeLookupField(){
      //console.log(`onChangeLookupField Index: ${this.index}. Type: ${this.concatenationValueitemType}.`);
    },
    /** Delete - remove Concatenation Value from the list. */
    onDeleteClick() {
      this.$emit('delete-concatenation-value', this.index);
    },
  },

  /** Component's Reactive State is initialised but DOM not yet rendered. */
  beforeMount() {
  },

};
</script>

<style lang="scss" scoped>
@import "@/scss/variables";
.concatenation-item {
  /* Item drag handle. */
  .handle {
    width: $table-builder-field-handle-width;
    min-width: $table-builder-field-handle-width;
    text-align: center;
    cursor: move;
  }
}
tr.concatenation-item:hover{
  background-color: transparent;
}
</style>
